import React, { useState, useEffect, useCallback } from 'react';
import { Table, Row, Col, Card, Spin, Pagination, Empty, ConfigProvider } from 'antd';

// Custom hook (pure JS, no deps)
const useMobileMediaQuery = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isMobile;
};

/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
const ResponsiveTable = React.memo(({
  columns,
  dataSource = [],
  pagination = false,
  loading = false,
  rowKey = 'id',
  scroll = { x: true },
  onChange,
  rowClassName,
  className,
  locale = { emptyText: 'No data' },
  size = 'middle',
  ...restProps
}) => {
  const isMobile = useMobileMediaQuery();

  const handlePaginationChange = useCallback((page, pageSize) => {
    onChange?.({ current: page, pageSize }, '', {});
  }, [onChange]);

  const paginationProps = typeof pagination === 'object' ? pagination : { 
    showSizeChanger: true, 
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    ...(typeof pagination === 'object' ? pagination : {})
  };

  if (!isMobile) {
    // Desktop: standard table
    return (
      <ConfigProvider>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={pagination}
            rowKey={rowKey}
            scroll={scroll}
            loading={false}
            onChange={onChange}
            rowClassName={rowClassName}
            className={className}
            locale={locale}
            size={size}
            {...restProps}
          />
        </Spin>
      </ConfigProvider>
    );
  }

  // Mobile: Cards view
  const total = paginationProps.total || dataSource.length;
  const pageSize = paginationProps.pageSize || 10;
  const currentPage = paginationProps.current || 1;
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedData = dataSource.slice(startIdx, startIdx + pageSize);

  const renderCell = (column, record) => {
    let content;
    if (column.render) {
      content = column.render(record[column.dataIndex], record, 0);
    } else {
      content = record[column.dataIndex] || '-';
    }
    return (
      <div key={column.key || column.dataIndex} style={{ marginBottom: 12, minHeight: 20 }}>
        {column.title ? (
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 600, 
            color: '#6b7280', 
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 4 
          }}>
            {column.title}
          </div>
        ) : null}
        <div style={{ 
          fontSize: '14px', 
          color: '#1a1d2e',
          wordBreak: 'break-word',
          lineHeight: 1.4 
        }}>
          {content}
        </div>
      </div>
    );
  };

  // Fixed: Added return statement and proper JSX structure
  const CardItem = React.memo(({ record, index }) => {
    return (
      <Col xs={24} sm={12} md={12} lg={12} xl={12} key={record[rowKey] || index}>
        <Card 
          style={{ 
            borderRadius: 12, 
            border: '1px solid #f0f4ff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: 16,
            height: '100%'
          }}
          bodyStyle={{ padding: '16px' }}
          hoverable
        >
          <div style={{ fontSize: 12, color: '#7B4D12', marginBottom: 12 }}>
            Row #{startIdx + index + 1}
          </div>
          <Row gutter={[0, 8]}>
            {columns.map((column, colIdx) => (
              <Col xs={24} sm={12} key={colIdx} style={{ padding: '0 8px' }}>
                {renderCell(column, record)}
              </Col>
            ))}
          </Row>
        </Card>
      </Col>
    );
  });

  CardItem.displayName = 'CardItem';

  return (
    <div>
      <Spin spinning={loading}>
        {total === 0 ? (
          <Empty description={locale.emptyText} style={{ margin: '40px 0' }} />
        ) : (
          <>
            {/* Pagination on top for mobile */}
            {pagination && (
              <div style={{ marginBottom: 16, textAlign: 'center' }}>
                <Pagination
                  {...paginationProps}
                  onChange={handlePaginationChange}
                  size="small"
                  showSizeChanger={false}
                  simple={dataSource.length > 5}
                />
              </div>
            )}
            
            <Row gutter={[16, 16]}>
              {paginatedData.map((record, index) => (
                <CardItem 
                  record={record} 
                  index={index} 
                  key={record[rowKey] || index} 
                />
              ))}
            </Row>
            
            {/* Pagination on bottom */}
            {pagination && dataSource.length > pageSize && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Pagination
                  {...paginationProps}
                  onChange={handlePaginationChange}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );
});

ResponsiveTable.displayName = 'ResponsiveTable';

export default ResponsiveTable;